import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { api } from "../../../lib/api";
import { ConfirmDialog } from "../../common/ConfirmDialog";
import { SavedNote } from "../../shared/Icons";
import { TYPE_LABEL, ghostBtn, type Field, type FieldType } from "../SelfService";
import { useUnsavedGuard } from "../unsavedGuard";
import { FieldEditor, chipsOf } from "./FieldEditor";
import { stable, uid, uniqueKey, type Block } from "./layout";

// Aba "Campos" do Agente Global (entre Prompt e Ordem): a coleção de campos
// editáveis (agents.ss_fields), desacoplada das sessões. O chip {{ campo.chave }}
// entra em qualquer sessão pelo botão "Campos editáveis" do editor da aba Prompt;
// aqui o clique no chip só copia. A posição no portal é na aba Ordem.

const card: React.CSSProperties = { background: "var(--surface-hover)", border: "1px solid var(--line)", borderRadius: 10, padding: 14, display: "flex", flexDirection: "column", gap: 10 };
const redBtn: React.CSSProperties = { padding: "8px 16px", background: "var(--brand)", border: "none", borderRadius: 8, color: "#fff", fontSize: 13, cursor: "pointer" };

type Sec = { id: string; name: string; content: string };
const esc = (s: string) => s.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
const chipRe = (key: string) => new RegExp(`\\{\\{\\s*campo\\.${esc(key)}(_(\\d{2}|outro))?\\s*\\}\\}`, "g");
const ANY_CHIP = /\{\{\s*campo\.([A-Za-z0-9_]+?)(_\d{2}|_outro)?\s*\}\}/g;

/** Chaves renomeadas (velha → nova) trocadas numa passada só: em cadeia, a→b e b→c cruzariam os chips. */
const renameChips = (content: string, map: Map<string, string>) =>
  content.replace(ANY_CHIP, (m, base, suf) => (map.has(base) ? `{{ campo.${map.get(base)}${suf ?? ""} }}` : m));

/** Onde o campo é usado (pela chave SALVA): sessões do prompt (pelo chip) e bloco da Ordem (pela célula). */
function usage(key: string, id: string, sections: Sec[], blocks: Block[]): string[] {
  const secs = sections.filter((s) => chipRe(key).test(s.content ?? "")).map((s) => `sessão “${s.name}”`);
  const block = blocks.find((b) => (b.rows ?? []).some((r) => r.some((c) => c.session_id === id)));
  return [...secs, ...(block ? [`bloco “${block.title || block.key}” da Ordem`] : [])];
}

export function CamposTab({ agent }: { agent: any }) {
  const qc = useQueryClient();
  const saved: Field[] = agent.ss_fields ?? [];
  const [fields, setFields] = useState<Field[]>(saved);
  const [openId, setOpenId] = useState<string | null>(null);
  const [removing, setRemoving] = useState<Field | null>(null);
  const sections: Sec[] = agent.prompt_sections ?? [];  // system_message incluída: o chip vale lá também
  const keys = new Set(fields.map((f) => f.key));

  // Chave renomeada → reescreve {{ campo.x }} / x_01 / x_outro nas sessões salvas (pareado por id).
  const renames = new Map<string, string>();
  for (const f of fields) { const old = saved.find((o) => o.id === f.id)?.key; if (old && f.key && old !== f.key) renames.set(old, f.key); }
  const payload = () => ({
    ss_fields: fields,
    ...(renames.size ? { prompt_sections: sections.map((s) => ({ ...s, content: renameChips(s.content ?? "", renames) })) } : {}),
  });
  // stable(): o JSONB volta com as chaves em outra ordem — JSON.stringify cru ficaria "sujo" pra sempre
  const dirty = stable(fields) !== stable(saved);
  const invalid = fields.some((f) => !f.key);
  const save = useMutation({
    mutationFn: () => api.patch(`/agents/${agent.id}`, payload()),
    onSuccess: async () => { await qc.invalidateQueries({ queryKey: ["agent", agent.id] }); },
  });
  useUnsavedGuard(dirty, save.mutateAsync);

  const add = (type: FieldType) => {
    let n = 1;
    while (keys.has(`campo_${n}`)) n++;
    const f: Field = { id: uid(), key: uniqueKey(`campo_${n}`, keys), label: "", type };
    setFields((p) => [...p, f]);
    setOpenId(f.id);
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <div style={{ ...card, background: "color-mix(in srgb, var(--warn-soft), transparent 55%)", borderColor: "var(--warn-line)" }}>
        <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
          <span style={{ fontSize: 12, fontWeight: 600, color: "var(--ink)" }}>Campos editáveis <span style={{ fontWeight: 400, color: "var(--ink-soft)" }}>· o cliente preenche no portal; o chip entra em qualquer sessão pelo botão “Campos editáveis” do editor</span></span>
          <span style={{ flex: 1 }} />
          {(Object.keys(TYPE_LABEL) as FieldType[]).map((t) => (
            <button key={t} onClick={() => add(t)} style={{ ...ghostBtn, borderColor: "var(--warn-line)", color: "var(--warn)" }}>+ {TYPE_LABEL[t]}</button>
          ))}
        </div>
        {fields.length === 0 && <div style={{ fontSize: 12, color: "var(--ink-muted)" }}>Nenhum campo. Sem campo o portal do cliente fica vazio.</div>}
        {fields.map((f) => {
          const open = openId === f.id;
          return (
            <div key={f.id} style={{ ...card, padding: 10, gap: 8, background: "var(--surface-card)" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
                <button onClick={() => setOpenId(open ? null : f.id)} style={{ background: "none", border: "none", color: "var(--ink)", fontSize: 13, fontWeight: 500, cursor: "pointer", padding: 0 }}>
                  {open ? "▾" : "▸"} {f.label || <span style={{ color: "var(--ink-muted)" }}>sem rótulo</span>} <span style={{ fontSize: 11, color: "var(--ink-soft)" }}>· {TYPE_LABEL[f.type]}</span>
                </button>
                <span style={{ flex: 1 }} />
                {!open && chipsOf(f).map((c) => (
                  <button key={c} title="Copiar chip" onClick={() => navigator.clipboard?.writeText(c).catch(() => {})} style={{ fontFamily: "var(--font-mono)", fontSize: 11, padding: "2px 8px", borderRadius: 6, background: "var(--warn-soft)", color: "var(--warn)", border: "1px solid var(--warn-line)", cursor: "pointer" }}>{c.replace(/[{} ]/g, "").slice(6)}</button>
                ))}
              </div>
              {open && (
                <FieldEditor f={f} taken={new Set([...keys].filter((k) => k !== f.key))}
                  onChange={(patch) => setFields((p) => p.map((x) => (x.id === f.id ? { ...x, ...patch } : x)))}
                  onRemove={() => setRemoving(f)}
                  onInsert={null} />
              )}
            </div>
          );
        })}
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
        <button onClick={() => save.mutate()} disabled={save.isPending || !dirty || invalid} style={{ ...redBtn, opacity: dirty && !invalid ? 1 : 0.6 }}>{save.isPending ? "Salvando..." : "Salvar"}</button>
        <SavedNote show={save.isSuccess} />
        {invalid && <span style={{ fontSize: 11, color: "var(--danger)" }}>Todo campo precisa de uma chave de chip.</span>}
        {renames.size > 0 && <span style={{ fontSize: 11, color: "var(--ink-dim)" }}>Chave renomeada: os chips no prompt são reescritos ao salvar.</span>}
      </div>

      {removing && (() => {
        const where = usage(saved.find((o) => o.id === removing.id)?.key ?? removing.key, removing.id, sections, agent.ss_blocks ?? []);
        return <ConfirmDialog title={`Excluir “${removing.label || removing.key}”?`} danger confirmLabel="Excluir"
          message={where.length ? `Usado em ${where.join(", ")}. O chip fica vermelho no prompt e vazio no agente; a célula some da Ordem. As respostas dos clientes para este campo se perdem.` : "Nenhum uso no prompt nem na Ordem. As respostas dos clientes para este campo se perdem."}
          onConfirm={() => { setFields((p) => p.filter((x) => x.id !== removing.id)); setOpenId(null); setRemoving(null); }} onCancel={() => setRemoving(null)} />;
      })()}
    </div>
  );
}
